import { debounce } from '@theme/global';

const RECENT_KEY = 'fivl:recent-searches';
const MAX_RECENT = 6;

/**
 * Instant search. Fetches the predictive-search section while typing, shows
 * an empty state (recent, popular, trending) before typing, remembers recent
 * searches and supports arrow-key navigation through results.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.form = this.input?.form;
    this.results = this.querySelector('#PredictiveSearchResults');
    this.empty = this.querySelector('[data-search-empty]');
    this.reset = this.querySelector('button[type="reset"]');
    this.spinner = this.querySelector('.loading-spinner');
    this.cache = new Map();
    this.enabled = !this.hasAttribute('disabled');

    this.renderRecent();
    this.input.addEventListener('input', debounce(() => this.onInput(), 220));
    this.input.addEventListener('focus', () => this.onInput());
    this.reset?.addEventListener('click', () => this.clear());
    this.form?.addEventListener('submit', () => this.remember(this.input.value));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('click', (event) => {
      const term = event.target.closest('[data-search-term]');
      if (term) {
        event.preventDefault();
        this.input.value = term.dataset.searchTerm;
        this.remember(term.dataset.searchTerm);
        this.onInput();
        this.input.focus();
        return;
      }
      if (event.target.closest('[data-clear-recent]')) {
        this.saveRecent([]);
        this.renderRecent();
        return;
      }
      const link = event.target.closest('.predictive-search a, .mini-card__title');
      if (link && this.input.value.trim()) this.remember(this.input.value);
    });
    this.closest('theme-dialog')?.addEventListener('dialog:open', () => this.onInput());
  }

  onInput() {
    const term = this.input.value.trim();
    this.reset?.toggleAttribute('hidden', term.length === 0);
    if (term.length < 2 || !this.enabled) {
      this.showEmpty();
      return;
    }
    this.search(term);
  }

  showEmpty() {
    this.results.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    if (this.empty) this.empty.hidden = false;
  }

  async search(term) {
    if (this.cache.has(term)) {
      this.render(this.cache.get(term));
      return;
    }
    this.spinner?.removeAttribute('hidden');
    this.abort?.abort();
    this.abort = new AbortController();
    try {
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,article,page,query',
        'resources[limit]': String(Math.max(Number(this.dataset.limit) || 6, 6)),
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': this.dataset.fields || 'title,product_type,variants.title,vendor,tag',
        section_id: 'predictive-search',
      });
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.abort.signal });
      if (!response.ok) throw new Error(response.status);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search')?.innerHTML || '';
      this.cache.set(term, html);
      if (this.input.value.trim() === term) this.render(html);
    } catch (error) {
      if (error.name !== 'AbortError') this.showEmpty();
    } finally {
      this.spinner?.setAttribute('hidden', '');
    }
  }

  render(html) {
    if (this.empty) this.empty.hidden = true;
    this.results.innerHTML = html;
    this.input.setAttribute('aria-expanded', 'true');
  }

  clear() {
    this.input.value = '';
    this.showEmpty();
    this.reset?.setAttribute('hidden', '');
    this.input.focus();
  }

  /* ---------- Recent searches ---------- */
  readRecent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }
  saveRecent(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (error) {
      /* storage unavailable */
    }
  }
  remember(term) {
    if (!this.hasAttribute('data-recent')) return;
    const clean = (term || '').trim();
    if (clean.length < 2) return;
    const list = [clean, ...this.readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase())].slice(0, MAX_RECENT);
    this.saveRecent(list);
    this.renderRecent();
  }
  renderRecent() {
    const wrap = this.querySelector('[data-recent-searches]');
    const list = this.querySelector('[data-recent-list]');
    if (!wrap || !list) return;
    const items = this.readRecent();
    wrap.hidden = items.length === 0;
    list.innerHTML = items
      .map((t) => `<button type="button" class="pill pill--small pill--soft" data-search-term="${this.escape(t)}">${this.escape(t)}</button>`)
      .join('');
  }
  escape(value) {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  /* ---------- Keyboard ---------- */
  onKeydown(event) {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const scope = this.results.innerHTML.trim() ? this.results : this.empty;
    const options = Array.from(scope?.querySelectorAll('.mini-card__title, .predictive-search__link, [data-search-term]') || []);
    if (!options.length) return;
    event.preventDefault();
    const current = options.indexOf(document.activeElement);
    let next = event.key === 'ArrowDown' ? current + 1 : current - 1;
    if (next < 0) {
      this.input.focus();
      return;
    }
    if (next >= options.length) next = 0;
    options[next].focus();
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
